import React from "react";
import { Snowflake, Trash2, TrendingDown, CheckCircle2, Zap, Sparkles } from "lucide-react";

export const ProblemSolution = () => {
    return (
        <section className="py-20 md:py-28 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Problem */}
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                    <div className="order-2 lg:order-1">
                        <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">
                            O Problema
                        </span>
                        <h2 className="mt-4 font-display font-medium text-4xl md:text-5xl text-brand-ink leading-tight">
                            Os sumos naturais de rua{" "}
                            <span className="text-brand-orange">têm um problema.</span>
                        </h2>
                        <p className="mt-5 text-lg text-brand-ink-soft leading-relaxed">
                            Operar com fruta fresca é caro, complexo e cheio de limites. O modelo
                            tradicional não escala.
                        </p>

                        <ul className="mt-8 space-y-4">
                            {[
                                {
                                    icon: Trash2,
                                    title: "Muito desperdício",
                                    text: "Fruta estraga-se em 2 dias. Margem evapora.",
                                },
                                {
                                    icon: Snowflake,
                                    title: "Qualidade irregular",
                                    text: "Depende da época, do fornecedor, do calor.",
                                },
                                {
                                    icon: TrendingDown,
                                    title: "Difícil de escalar",
                                    text: "Preparação manual, porções inconsistentes, stock imprevisível.",
                                },
                            ].map((p) => (
                                <li
                                    key={p.title}
                                    className="flex items-start gap-4 p-4 rounded-2xl bg-brand-cream"
                                >
                                    <div className="h-11 w-11 rounded-xl bg-white flex items-center justify-center shrink-0 shadow-soft">
                                        <p.icon className="h-5 w-5 text-brand-orange" />
                                    </div>
                                    <div>
                                        <div className="font-bold text-brand-ink">{p.title}</div>
                                        <div className="text-sm text-brand-ink-soft mt-0.5">
                                            {p.text}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="order-1 lg:order-2 relative">
                        <div className="relative rounded-2xl overflow-hidden shadow-pop">
                            <img
                                src="https://images.unsplash.com/photo-1610622930110-3c076902312a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                                alt="Cadeia de frio caótica"
                                className="w-full h-[420px] md:h-[520px] object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/40 via-transparent to-transparent" />
                            <div className="absolute bottom-6 left-6 right-6 text-white">
                                <div className="text-xs uppercase tracking-widest opacity-80">
                                    Modelo tradicional
                                </div>
                                <div className="font-display font-medium text-2xl mt-1">
                                    Gelo, arcas, caos.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Solution */}
                <div className="mt-28 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                    <div className="relative">
                        <div className="absolute -inset-4 bg-brand-green/10 rounded-[3rem] blur-2xl" />
                        <div className="relative rounded-2xl overflow-hidden shadow-pop">
                            <img
                                src="https://images.unsplash.com/photo-1593429109417-c18f96abce98?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                                alt="Solução Sabor Líquido"
                                className="w-full h-[420px] md:h-[520px] object-cover"
                            />
                            <div className="absolute top-6 left-6 inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-pop">
                                <Sparkles className="h-4 w-4 text-brand-orange" />
                                <span className="font-bold text-sm text-brand-ink">
                                    Tecnologia de fruta
                                </span>
                            </div>
                        </div>
                    </div>

                    <div>
                        <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-brand-green">
                            A Solução
                        </span>
                        <h2 className="mt-4 font-display font-medium text-4xl md:text-5xl text-brand-ink leading-tight">
                            Um sistema inteligente de sumos.{" "}
                            <span className="text-brand-green">Simples assim.</span>
                        </h2>
                        <p className="mt-5 text-lg text-brand-ink-soft leading-relaxed">
                            Polpa de fruta congelada — conservada a -18 °C, fruta da época
                            seleccionada no pico de maturação. Adiciona-se água. Mistura-se. Pronto.
                            Sumo natural, fresco, em segundos.
                        </p>

                        <ul className="mt-8 space-y-4">
                            {[
                                {
                                    icon: CheckCircle2,
                                    title: "Polpa de fruta congelada",
                                    text: "Fruta no pico de maturação, conservada a -18 °C.",
                                },
                                {
                                    icon: Zap,
                                    title: "Preparação instantânea",
                                    text: "Porção + água + liquidificador. Pronto em 30 segundos.",
                                },
                                {
                                    icon: Snowflake,
                                    title: "Qualidade constante",
                                    text: "Mesmo sabor todo o ano. Porções exactas. Zero desperdício.",
                                },
                            ].map((p) => (
                                <li
                                    key={p.title}
                                    className="flex items-start gap-4 p-4 rounded-2xl bg-brand-green-soft"
                                >
                                    <div className="h-11 w-11 rounded-xl bg-brand-green flex items-center justify-center shrink-0">
                                        <p.icon className="h-5 w-5 text-white" />
                                    </div>
                                    <div>
                                        <div className="font-bold text-brand-ink">{p.title}</div>
                                        <div className="text-sm text-brand-ink-soft mt-0.5">
                                            {p.text}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
};
